import type { Span } from '@sentry/react'
import EventEmitter from 'events'
import {
  setSpanOutcome,
  startFlowSpan,
  TracedFlow,
} from '~/services/observability/tracing'
import { AnalyticsNamespace } from '~/types/Analytics'
import { SipClientAnalyticsEvents } from '~/types/Analytics/SipClientAnalyticsEvents'
import { Analytics } from '~/utils/analytics'

import {
  Invitation,
  Inviter,
  Notification,
  Registerer,
  RegistererState,
  RequestPendingError,
  Session,
  SessionInviteOptions,
  SessionState,
  Subscriber,
  SubscriptionState,
  UserAgent,
  UserAgentDelegate,
  UserAgentOptions,
} from '@mangovoice/sip'
import {
  IncomingResponse,
  OutgoingInfoRequest,
  OutgoingInviteRequest,
  OutgoingRequestDelegate,
} from '@mangovoice/sip/core'
import { SessionDescriptionHandler } from '@mangovoice/sip/platform/web'
import { Logger, SipConnectionInfo } from '@mangovoice/types'

import { CallQualityMonitor } from './callQuality/CallQualityMonitor'
import { CallDirection } from './callQuality/types'
import { buildEnvironment } from './common'
import { isClickToCallName } from './functions'
import {
  CallTransport,
  mapRegistrationStateToEvent,
  mapSessionTransitionToEvent,
  SipAnalyticsEvent,
} from './sipAnalytics'
import {
  ConnectionState,
  ConnectionStateGuard,
} from './sipclient-connection-guard'
import { getPresenceStateFromNotify, PresenceState } from './xml'

const WSS_PORT = '7443'
const DTMF_DURATION = 250

/**
 * Thrown by `getSession`. Exported because callers match on it to tell an expected
 * "the call is already gone" outcome apart from a real failure — keep the throw site
 * and those callers on this constant so a reword cannot silently break the match.
 */
export const SESSION_NOT_FOUND_ERROR_MESSAGE = 'Session not found.'

/**
 * Thrown by checkConnection when the SIP UserAgent is not connected.
 * Callers that match against this string depend on this constant so that a
 * reword cannot silently break the match.
 */
export const USER_AGENT_NOT_CONNECTED_ERROR_MESSAGE = 'UserAgent not connected.'

/**
 * How long a hold/resume re-INVITE may stay unsettled before the per-call
 * concurrency guard is force-released.
 *
 * The case this covers: sip.js clears Timer B as soon as the transaction
 * leaves "Calling" (invite-client-transaction.ts), and implements no Timer C,
 * so a re-INVITE that gets a 100 Trying and then no final response has no
 * sip.js timeout at all and would leave the guard set for the life of the call.
 *
 * Must stay *above* Timer B (64 * T1 = 32s), not equal to it. When there is no
 * provisional response, Timer B synthesizes a 408 that reaches our onReject and
 * clears sip.js's own `pendingReinvite`; releasing first would let a new
 * re-INVITE through while that flag is still set, reproducing the error this
 * guard exists to prevent.
 */
const REINVITE_SETTLE_TIMEOUT_MS = 35_000

/**
 * How long a SIP UUID stays "recently terminated" after SessionState.Terminated
 * deletes the session. LINE_EXTENSION_HANGUP can arrive after that cleanup
 * (and after usePhone removeCall), so deskphone analytics uses this window to
 * avoid classifying the hangup as a deskphone failure.
 */
export const RECENTLY_TERMINATED_SIP_TTL_MS = 60_000

/** One outstanding hold/resume re-INVITE. The record doubles as its own identity token. */
interface PendingHold {
  timer: ReturnType<typeof setTimeout>
}

export interface SessionStateChange {
  callUuid: string
  state: SessionState
}

export interface OutboundCallProgressEvent {
  callUuid: string
  statusCode: number
}

export interface PresenceStateChange {
  subscribedNumber: string
  state: PresenceState
}

interface SipClientOptions {
  sipConnectionInfo: SipConnectionInfo
  userAgentString: string
  logger?: Logger
  analytics?: Analytics<AnalyticsNamespace.SIP_CLIENT>
}

export class SipClient
  extends EventEmitter
  implements UserAgentDelegate, OutgoingRequestDelegate
{
  private _ua: UserAgent
  private _sessions = new Map<string, Session>()
  private _sessionStates = new Map<string, SessionState>()
  private _subscriptions = new Map<string, Subscriber>()
  private _sipDomain: string
  private _logger?: Logger
  private _analytics?: Analytics<AnalyticsNamespace.SIP_CLIENT>
  private _registerer?: Registerer
  private _connectionGuard = new ConnectionStateGuard()
  private _processingStateChange = new Set<string>()
  private _pendingAnswers = new Set<string>()
  // Hold/resume re-INVITEs still awaiting a final response, keyed by callUuid,
  // valued by the Timer-B safety net. Lives on the client, not on the calling
  // hook: one SipClient is shared by every mounted usePhoneEndpoint (main
  // window Hold button, popup IPC, Yealink hook switch), and a per-hook ref
  // would give each of them its own guard and serialize nothing.
  private _pendingHoldOperations = new Map<string, PendingHold>()
  private _qualityMonitors = new Map<string, CallQualityMonitor>()
  // Call-setup tracing (PE-40): one span per outbound call, INVITE → media
  // established. An entry still here at Terminated means setup never reached
  // media. PHI rule: attributes never carry dialed numbers or display names.
  private _callSetupSpans = new Map<
    string,
    { span: Span; startedAtMs: number; ringingAtMs?: number }
  >()
  // Calls that reached Established, so a Terminated transition can tell a
  // hangup after a real conversation from a setup that never got to media.
  // `_sessionStates` can't answer this — it holds only the current state.
  private _establishedCalls = new Set<string>()
  // Calls this client tore down itself. SIP.js only routes an outbound cancel
  // through `Terminating`; `Invitation.reject()` and an incoming CANCEL both
  // go straight to `Terminated`, so the state machine alone cannot tell an
  // inbound call rejected while answering from one whose answer failed.
  private _locallyTerminatedCalls = new Set<string>()
  // Inbound calls that received a CANCEL. A CANCEL during `accept()` also
  // moves `Establishing` to `Terminated`, the same as a failed answer.
  private _remotelyCanceledCalls = new Set<string>()
  // UUIDs whose SIP session was just torn down, timestamped so a later
  // chat-rtm HANGUP can still tell a softphone call from a deskphone one.
  private _recentlyTerminatedCalls = new Map<string, number>()

  constructor(options: SipClientOptions) {
    super()
    const { sipConnectionInfo, userAgentString, logger, analytics } = options

    if (logger) this._logger = logger
    if (analytics) this._analytics = analytics
    this._sipDomain = sipConnectionInfo.domain
    this._connectionGuard.transitionTo(ConnectionState.CONNECTING)
    this._ua = new UserAgent(
      this.configureUserAgent(sipConnectionInfo, userAgentString),
    )
    this._ua.delegate = this
    // Analytics policy: SIP operations emit failure events only. Success rates
    // come from the curated lifecycle events (sipAnalytics.ts), not from
    // per-operation request/success pairs, which flooded PostHog with 2-3
    // events per user action.
    this._ua.start().catch((e) => {
      if (this._connectionGuard.canTransition) {
        this._analytics?.track(SipClientAnalyticsEvents.WssConnFailure)
        this._logger?.error('Failed to start UserAgent', e as Error)
        // SIP.js reports a disconnect only when leaving Connected, so a first
        // connect that never opens has to be reported here.
        this._connectionGuard.transitionTo(ConnectionState.DISCONNECTED)
        this.emit('TransportDisconnected', e as Error)
      }
    })
  }

  /**
   * Tears down Sessions, Transport, and UA
   * @returns
   */
  public dispose(): Promise<void> {
    if (this._connectionGuard.isDisposed) {
      this._logger?.debug('SipClient already disposed')
      return Promise.resolve()
    }

    this._connectionGuard.transitionTo(ConnectionState.DISCONNECTING)
    this._logger?.info('Disposing SipClient')

    // Flush any in-flight call quality monitors so their summaries are emitted.
    this.stopAllQualityMonitors()
    this.clearAllPendingHolds()

    // Close any span still open; disposal mid-setup is a cancel. Without this
    // the span never ends and Sentry drops it, losing the setup attempt.
    for (const callUuid of [...this._callSetupSpans.keys()]) {
      this.endCallSetupSpan(callUuid, 'canceled')
    }
    this._establishedCalls.clear()
    this._locallyTerminatedCalls.clear()
    this._remotelyCanceledCalls.clear()
    this._recentlyTerminatedCalls.clear()

    try {
      this._connectionGuard.abort()
      return this._ua.stop()
    } catch (error: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.UnregFailure)
      const normalizedError =
        error instanceof Error ? error : new Error(String(error))
      this._logger?.error('Failed to dispose SipClient', normalizedError)
      return Promise.reject(normalizedError)
    }
  }

  /**
   * Randomly choose a SIP server to provide basic load balancing when useInstance is false
   * @param sipConnection
   * @returns
   */
  private chooseRandomServer = (sipConnection: SipConnectionInfo): string => {
    let server: { sip_host: string; sip_port: number }

    const params = new URLSearchParams(window.location.search)
    const useInstance = params.get('useInstance') ?? false
    const servers: { sip_host: string; sip_port: number }[] =
      sipConnection.registration_servers ?? []

    // Defence in depth: useGetSipConnectionInfo already rejects a response
    // with no registration servers, but this class is also constructed
    // directly, bypassing that query.
    if (servers.length === 0) {
      throw new Error('No SIP registration servers available')
    }

    if (useInstance) {
      const instanceIndex = parseInt(useInstance, 10)
      server = servers[instanceIndex] ?? servers[0]
    } else {
      server = servers[Math.floor(Math.random() * servers.length)]
    }

    return `wss://${server.sip_host}:${WSS_PORT}`
  }

  private configureUserAgent = (
    sipConnectionInfo: SipConnectionInfo,
    userAgentString: string,
  ): UserAgentOptions => {
    const isProduction = buildEnvironment === 'production'
    const sipUri = `sip:${sipConnectionInfo.username}@${sipConnectionInfo.domain}`
    const uri = UserAgent.makeURI(sipUri)
    if (!uri) {
      throw new Error('Failed to create URI during registration.')
    }
    return {
      uri,
      authorizationUsername: sipConnectionInfo.username,
      authorizationPassword: sipConnectionInfo.password,
      logConfiguration: !isProduction,
      logLevel: isProduction ? 'warn' : 'log',
      logConnector: (level, category, label, content) => {
        const logMessage = `${category}${label ?? ''} ${content}`
        switch (level) {
          case 'debug':
            this._logger?.debug(logMessage)
            break
          case 'log':
            this._logger?.info(logMessage)
            break
          case 'warn':
            this._logger?.warn(logMessage)
            break
          case 'error':
            this._logger?.warn(logMessage)
            break
        }
      },
      noAnswerTimeout: 60000,
      userAgentString,
      // Don't auto-send a 180 Ringing on every INVITE. The stack would emit it
      // before onInvite runs, so a busy-reject (486) would always be preceded by
      // a 180 — the agent's phone rings audibly even with "Respond with Busy to
      // Second Call" on (CUS-591). With this off we send the 180 ourselves only
      // on calls we actually ring (see progressCall).
      //
      // INVARIANT: this makes progressCall() the SOLE emitter of the inbound
      // 180. Any code path that rings an incoming call MUST call progressCall()
      // or the caller silently loses ringback. Today the only such path is the
      // IncomingInviteReceived handler in usePhone.
      sendInitialProvisionalResponse: false,
      transportOptions: {
        server: this.chooseRandomServer(sipConnectionInfo),
        connectionTimeout: 30, // Increased from 10 to 30 seconds
        keepAliveInterval: 15, // Decreased from 29 to 15 seconds
        keepAliveDebounce: 50, // Keep this the same
        traceSip: !isProduction,
        sendDtmf: true,
      },
    }
  }

  /**
   * onConnect builds a fresh Registerer per transport connection, so the id is
   * captured per listener rather than read off the client: a replaced Registerer
   * keeps its own refresh timer, and reporting the current id would make its
   * events indistinguishable from the live one's.
   */
  private registrationStateListener(
    registrationId: string,
  ): (newState: RegistererState) => void {
    return (newState: RegistererState): void => {
      if (this._connectionGuard.checkAborted()) {
        this._logger?.warn('registrationStateListener::Registration aborted')
        return
      }
      const regEvent = mapRegistrationStateToEvent(newState)
      if (regEvent) {
        this._analytics?.track(regEvent, { registration_id: registrationId })
      }
      this.emit('RegistrationStateChanged', newState)
    }
  }

  private outboundSessionStateListener(
    callUuid: string,
  ): (newState: SessionState) => void {
    return (newState: SessionState): void => {
      if (this._connectionGuard.checkAborted()) {
        this._logger?.warn('outboundSessionStateListener::Session aborted')
        return
      }

      const stateKey = `${callUuid}-${newState}`
      if (this._processingStateChange.has(stateKey)) {
        this._logger?.warn(`Preventing recursive state change: ${stateKey}`)
        return
      }

      const oldState = this._sessionStates.get(callUuid)
      if (oldState === newState) return

      this._processingStateChange.add(stateKey)
      try {
        this._logger?.info(
          `SessionStateChange Outbound: ${oldState} -> ${newState}`,
        )
        this._sessionStates.set(callUuid, newState)
        if (newState === SessionState.Established) {
          this._establishedCalls.add(callUuid)
        }
        const obEvent = mapSessionTransitionToEvent(newState, {
          direction: 'outbound',
          wasEstablished: this._establishedCalls.has(callUuid),
          locallyTerminated:
            this._locallyTerminatedCalls.has(callUuid) ||
            oldState === SessionState.Terminating,
        })
        if (obEvent) {
          this.trackCallLifecycle(obEvent.event, {
            direction: 'outbound',
            callUuid,
            connected: obEvent.connected,
            ...(obEvent.canceled === undefined
              ? {}
              : { canceled: obEvent.canceled }),
          })
        }
        if (newState === SessionState.Terminated) {
          this.rememberTerminatedSession(callUuid)
        }
        this.emit('OutboundSessionStateChange', {
          callUuid,
          state: newState,
        } as SessionStateChange)

        if (newState === SessionState.Established) {
          this.endCallSetupSpan(callUuid, 'connected')
          this.startQualityMonitor(callUuid, 'outbound')
        }

        // Clean up session from maps when terminated to prevent memory leaks
        // and allow detecting orphaned calls
        if (newState === SessionState.Terminated) {
          // A span still open here means media was never established, so this
          // splits the two ways that happens: the local side gave up while it
          // rang (Terminating) versus the call failing on its own.
          this.endCallSetupSpan(
            callUuid,
            oldState === SessionState.Terminating ? 'canceled' : 'failed',
          )
          this.stopQualityMonitor(callUuid)
          this.endPendingHold(callUuid)
          this._establishedCalls.delete(callUuid)
          this._locallyTerminatedCalls.delete(callUuid)
          this._sessions.delete(callUuid)
          this._sessionStates.delete(callUuid)
          this._logger?.info(
            `Session cleaned up from maps after termination: ${callUuid}`,
          )
        }
      } finally {
        this._processingStateChange.delete(stateKey)
      }
    }
  }

  private trackCallLifecycle(
    event: SipClientAnalyticsEvents,
    properties: {
      direction: 'inbound' | 'outbound'
      callUuid: string
      connected?: boolean
      canceled?: boolean
    },
  ): void {
    this._analytics?.track(event, {
      ...properties,
      transport: CallTransport.Softphone,
    })
  }

  /**
   * Ends the call-setup span if one is still open for this call. The entry is
   * removed at Established, so a normal hangup's Terminated no-ops here.
   */
  private endCallSetupSpan(
    callUuid: string,
    outcome: 'connected' | 'canceled' | 'failed',
  ): void {
    const setup = this._callSetupSpans.get(callUuid)
    if (!setup) return

    this._callSetupSpans.delete(callUuid)
    setup.span.setAttribute('sip.setup_outcome', outcome)
    // A cancel is a user decision, not an error, so only 'failed' is a failure.
    setSpanOutcome(setup.span, outcome !== 'failed')
    setup.span.end()
  }

  private inboundSessionStateListener(
    callUuid: string,
  ): (newState: SessionState) => void {
    return (newState: SessionState): void => {
      if (this._connectionGuard.checkAborted()) {
        this._logger?.warn('inboundSessionStateListener::Session aborted')
        return
      }

      const stateKey = `${callUuid}-${newState}`
      if (this._processingStateChange.has(stateKey)) {
        this._logger?.warn(`Preventing recursive state change: ${stateKey}`)
        return
      }

      const oldState = this._sessionStates.get(callUuid)
      if (oldState === newState) return

      this._processingStateChange.add(stateKey)
      try {
        this._logger?.info(
          `SessionStateChange Inbound: ${oldState} -> ${newState}`,
        )
        this._sessionStates.set(callUuid, newState)
        if (newState === SessionState.Established) {
          this._establishedCalls.add(callUuid)
        }
        const inEvent = mapSessionTransitionToEvent(newState, {
          direction: 'inbound',
          wasEstablished: this._establishedCalls.has(callUuid),
          locallyTerminated:
            this._locallyTerminatedCalls.has(callUuid) ||
            oldState === SessionState.Terminating,
          answerAttempted:
            oldState === SessionState.Establishing &&
            !this._remotelyCanceledCalls.has(callUuid),
        })
        if (inEvent) {
          this.trackCallLifecycle(inEvent.event, {
            direction: 'inbound',
            callUuid,
            connected: inEvent.connected,
            ...(inEvent.canceled === undefined
              ? {}
              : { canceled: inEvent.canceled }),
          })
        }
        if (newState === SessionState.Terminated) {
          this.rememberTerminatedSession(callUuid)
        }
        this.emit('InboundSessionStateChange', {
          callUuid,
          state: newState,
        } as SessionStateChange)

        if (newState === SessionState.Established) {
          this.startQualityMonitor(callUuid, 'inbound')
        }

        // Clean up session from maps when terminated to prevent memory leaks
        // and allow detecting orphaned calls
        if (newState === SessionState.Terminated) {
          this.stopQualityMonitor(callUuid)
          this.endPendingHold(callUuid)
          this._sessions.delete(callUuid)
          this._sessionStates.delete(callUuid)
          this._pendingAnswers.delete(callUuid)
          this._establishedCalls.delete(callUuid)
          this._locallyTerminatedCalls.delete(callUuid)
          this._remotelyCanceledCalls.delete(callUuid)
          this._logger?.info(
            `Session cleaned up from maps after termination: ${callUuid}`,
          )
        }
      } finally {
        this._processingStateChange.delete(stateKey)
      }
    }
  }

  /**
   * Verifies the UserAgent is available and connected.
   * @param throwOnDisconnect - If true, throws an error when disconnected. If false, returns false.
   * @returns true if connected, false if disconnected (only when throwOnDisconnect is false)
   */
  private verifyUserAgent(throwOnDisconnect = true): boolean {
    if (this._connectionGuard.isDisposed) {
      this._logger?.warn('verifyUserAgent::UserAgent already disposed')
      if (!throwOnDisconnect) {
        return false
      }
      // When throwOnDisconnect is true, we still return false here
      // but the caller expects an exception, so this shouldn't be reached
      return false
    }
    if (!this._ua.isConnected()) {
      if (throwOnDisconnect) {
        throw new Error(USER_AGENT_NOT_CONNECTED_ERROR_MESSAGE)
      }
      return false
    }
    return true
  }

  /**
   * Throwing lookup for operations that require an existing call (hold,
   * transfer, DTMF). Invitation-lifecycle methods (answer/reject/progress)
   * race the remote side hanging up, so they use a null-safe
   * `_sessions.get` and no-op instead of calling this.
   */
  private getSession(callUuid: string): Session {
    this.verifyUserAgent()
    const session = this._sessions.get(callUuid)

    if (!session) {
      throw new Error(SESSION_NOT_FOUND_ERROR_MESSAGE)
    }
    return session
  }

  /**
   * Builds the request URI for a dial target, which must already have been
   * resolved by `toDialTarget`.
   *
   * A `#` reaching here is a bug in the caller, not something to encode away:
   * `#` terminates a dial string rather than naming a target, so there is no
   * `#` namespace on the switch to route to. Percent-encoding it does make
   * `makeURI` succeed, but the switch then answers the INVITE/REFER with a 202
   * and drops the caller with nothing the client can observe. Throwing keeps
   * the failure visible instead.
   */
  private buildDialUri(target: string): string {
    if (!target || target.includes('#')) {
      throw new Error(`Unresolved dial target: ${target}`)
    }
    return `sip:${target}@${this._sipDomain}`
  }

  private hasPeerConnection(callUuid: string): boolean {
    const session = this._sessions.get(callUuid)
    if (!session) return false
    const sdpHandler = session.sessionDescriptionHandler as
      SessionDescriptionHandler | undefined
    return !!sdpHandler?.peerConnection
  }

  private getSdpHandler(
    callUuid: string,
  ): SessionDescriptionHandler | undefined {
    const session = this.getSession(callUuid)
    return session.sessionDescriptionHandler as
      SessionDescriptionHandler | undefined
  }

  private getPeerConnection(callUuid: string) {
    const sdpHandler = this.getSdpHandler(callUuid)
    if (!sdpHandler?.peerConnection) {
      throw new Error('Peer connection not found.')
    }
    return sdpHandler.peerConnection
  }

  /**
   * Begin sampling WebRTC stats for a freshly-established call. No-op if a
   * monitor is already running for this call or the peer connection isn't ready.
   * Resulting events flow through analytics and are gated by the PostHog
   * capture kill-switch.
   */
  private startQualityMonitor(
    callUuid: string,
    direction: CallDirection,
  ): void {
    // Quality monitoring is best-effort telemetry — it must never throw out of
    // a session-state listener and disrupt call handling.
    try {
      // Already monitoring this call — leave it. A stale, self-stopped monitor
      // (e.g. after consecutive getStats failures) is replaced so a recovered
      // call can be monitored again.
      if (this._qualityMonitors.get(callUuid)?.isRunning) return
      if (!this.hasPeerConnection(callUuid)) return

      const monitor = new CallQualityMonitor({
        callUuid,
        direction,
        getPeerConnection: () => this.getPeerConnection(callUuid),
        analytics: this._analytics,
        logger: this._logger,
        // Drop our reference when the monitor stops itself (peer connection
        // closed/failed or repeated getStats failures) without a clean
        // Terminated, so the map can't leak stopped monitors. Identity-guarded
        // so a self-stop can't evict a newer monitor for the same call.
        onStop: () => {
          if (this._qualityMonitors.get(callUuid) === monitor) {
            this._qualityMonitors.delete(callUuid)
          }
        },
      })
      this._qualityMonitors.set(callUuid, monitor)
      monitor.start()
    } catch (e) {
      this._logger?.warn(`Failed to start call quality monitor: ${String(e)}`)
    }
  }

  /** Stop the monitor for a call (emits its summary) and drop it from the map. */
  private stopQualityMonitor(callUuid: string): void {
    const monitor = this._qualityMonitors.get(callUuid)
    if (!monitor) return
    try {
      monitor.stop()
    } catch (e) {
      this._logger?.warn(`Failed to stop call quality monitor: ${String(e)}`)
    }
    this._qualityMonitors.delete(callUuid)
  }

  /**
   * Stop every active monitor (each emits its summary). Called from dispose()
   * on full teardown. Per-call termination goes through stopQualityMonitor; a
   * connection that dies without a clean Terminated is caught by the monitor's
   * own self-stop (peer-connection closed/failed or repeated getStats failure).
   */
  private stopAllQualityMonitors(): void {
    for (const callUuid of [...this._qualityMonitors.keys()]) {
      this.stopQualityMonitor(callUuid)
    }
  }

  /**
   * Claim the hold guard for a call and arm the Timer-B release. The returned
   * record is the identity token for {@link endPendingHold}.
   */
  private beginPendingHold(callUuid: string): PendingHold {
    const pendingHold: PendingHold = {
      timer: setTimeout(() => {
        if (this._pendingHoldOperations.get(callUuid) !== pendingHold) return
        this._pendingHoldOperations.delete(callUuid)
        this._logger?.warn(
          `holdCall: re-INVITE for UUID=${callUuid} never settled within ${REINVITE_SETTLE_TIMEOUT_MS}ms, releasing hold guard`,
        )
      }, REINVITE_SETTLE_TIMEOUT_MS),
    }
    this._pendingHoldOperations.set(callUuid, pendingHold)
    return pendingHold
  }

  /**
   * Release the hold guard for a call once its re-INVITE transaction settles.
   * Pass the token from beginPendingHold to identity-guard the release, so a
   * final response arriving after that re-INVITE already hit the Timer-B
   * release can't free a newer hold's guard (same shape as the _qualityMonitors
   * self-stop above). Omit it on teardown paths, which release unconditionally.
   */
  private endPendingHold(callUuid: string, token?: PendingHold): void {
    const pendingHold = this._pendingHoldOperations.get(callUuid)
    if (!pendingHold) return
    if (token && pendingHold !== token) return
    clearTimeout(pendingHold.timer)
    this._pendingHoldOperations.delete(callUuid)
  }

  private clearAllPendingHolds(): void {
    for (const callUuid of [...this._pendingHoldOperations.keys()]) {
      this.endPendingHold(callUuid)
    }
  }

  public subscribeToParking(parkTarget: string): void {
    if (!this.verifyUserAgent(false)) {
      this._logger?.warn(
        `subscribeToParking: UserAgent not available, skipping subscription to ${parkTarget}`,
      )
      return
    }

    const existingSubscription = this._subscriptions.get(parkTarget)
    if (existingSubscription) {
      void existingSubscription.subscribe()
      this._logger?.info(
        `SIP SUBSCRIBE: Resubscribed to parking lot ${parkTarget}`,
      )
      return
    }

    const uri = UserAgent.makeURI(`sip:park+${parkTarget}@${this._sipDomain}`)
    if (!uri) {
      throw new Error('Failed to create URI for park subscription')
    }

    const subscriber = new Subscriber(this._ua, uri, 'dialog')
    subscriber.delegate = {
      onNotify: (notification: Notification) => {
        void notification.accept()

        const dialogInfo = notification.request.body
        const state = getPresenceStateFromNotify(dialogInfo)

        this.emit('ParkingStateChanged', {
          subscribedNumber: parkTarget,
          state: state,
        } as PresenceStateChange)
      },
    }
    subscriber.stateChange.addListener((newState: SubscriptionState) => {
      if (newState === SubscriptionState.Terminated) {
        this.emit('ParkingStateChanged', {
          subscribedNumber: parkTarget,
          state: PresenceState.Unknown,
        } as PresenceStateChange)
      }
    })
    subscriber.subscribe()
    this._logger?.info(`SIP SUBSCRIBE: Subscribed to parking lot ${parkTarget}`)
    this._subscriptions.set(parkTarget, subscriber)
  }

  public subscribeToExtension(extensionUuid: string): void {
    if (!this.verifyUserAgent(false)) {
      this._logger?.warn(
        `subscribeToExtension: UserAgent not available, skipping subscription to ${extensionUuid}`,
      )
      return
    }

    const existingSubscription = this._subscriptions.get(extensionUuid)
    if (existingSubscription) {
      existingSubscription.subscribe()
      this._logger?.info(
        `SIP SUBSCRIBE: Resubscribed to extension ${extensionUuid}`,
      )
      return
    }

    const uri = UserAgent.makeURI(`sip:${extensionUuid}@${this._sipDomain}`)
    if (!uri) {
      throw new Error('Failed to create URI for extension subscription')
    }

    const subscriber = new Subscriber(this._ua, uri, 'dialog')
    subscriber.delegate = {
      onNotify: (notification: Notification) => {
        notification.accept()

        const dialogInfo = notification.request.body
        const state = getPresenceStateFromNotify(dialogInfo)

        this.emit('ExtensionStateChanged', {
          subscribedNumber: extensionUuid,
          state: state,
        } as PresenceStateChange)
      },
    }
    subscriber.stateChange.addListener((newState: SubscriptionState) => {
      if (newState === SubscriptionState.Terminated) {
        this.emit('ExtensionStateChanged', {
          subscribedNumber: extensionUuid,
          state: PresenceState.Unknown,
        } as PresenceStateChange)
      }
    })
    subscriber.subscribe()
    this._logger?.info(
      `SIP SUBSCRIBE: Subscribed to extension ${extensionUuid}`,
    )
    this._subscriptions.set(extensionUuid, subscriber)
  }

  /**
   * Get the audio stream coming from the connected call
   * @param callUuid
   * @returns
   */
  public getRemoteStream(callUuid: string): MediaStream {
    const remoteStream = new MediaStream()
    try {
      const peerConnection = this.getPeerConnection(callUuid)
      peerConnection.getReceivers().forEach((receiver) => {
        remoteStream.addTrack(receiver.track)
      })
      return remoteStream
    } catch {
      const session = this._sessions.get(callUuid)
      if (!session) {
        this._logger?.warn(
          'Failed to get inbound audio stream: session not found',
        )
        return remoteStream
      }
      if (session.state !== SessionState.Established) {
        this._logger?.warn(
          'Failed to get inbound audio stream: session not established',
        )
        return remoteStream
      }
      throw new Error('Failed to get inbound audio stream')
    }
  }

  /**
   * Replace the sending stream of the peer connection identified by callUuid
   * with the provided stream
   * @param callUuid
   * @param stream
   * @returns a promise resolving to true if the track was attached to the
   * sender. Callers that pass a disposable track (e.g. the single-call mic
   * clone) rely on this to know whether to keep the track alive or stop it — a
   * silent no-op resolves false so the caller can dispose the orphaned track
   * instead of leaking it.
   */
  public async setRemoteStream(
    callUuid: string,
    stream: MediaStream,
  ): Promise<boolean> {
    try {
      const peerConnection = this.getPeerConnection(callUuid)
      peerConnection.getSenders()[0].track?.stop()
      // Await the replace: it returns a Promise that rejects if the track can't
      // be attached. Without the await the rejection escapes this try/catch and
      // we'd return true for a track that never reached the sender.
      await peerConnection.getSenders()[0].replaceTrack(stream.getTracks()[0])
      return true
    } catch {
      const session = this._sessions.get(callUuid)
      if (!session) {
        this._logger?.warn(
          'Failed to set outbound audio stream: session not found',
        )
        return false
      }
      if (session.state !== SessionState.Established) {
        this._logger?.warn(
          'Failed to set outbound audio stream: session not established',
        )
        return false
      }
      throw new Error('Failed to set outbound audio stream')
    }
  }

  public isConnected() {
    return !this._connectionGuard.isDisposed && this._ua.isConnected()
  }

  public hasPendingInvitation(callUuid: string): boolean {
    const pendingInvitation = Array.from(this._sessions.values())
      .filter((session) => session instanceof Invitation)
      .find((invitation) => invitation.id.startsWith(callUuid))
    return !!pendingInvitation
  }

  /**
   * Check if an active SIP session exists for the given call UUID
   * Returns false for terminated sessions even if they haven't been cleaned up yet
   * @param callUuid
   * @returns true if active session exists, false otherwise
   */
  public hasSession(callUuid: string): boolean {
    const session = this._sessions.get(callUuid)
    if (!session) return false
    // Only return true if session is not terminated
    return session.state !== SessionState.Terminated
  }

  /**
   * True when this UUID had a SIP session that Terminated within
   * {@link RECENTLY_TERMINATED_SIP_TTL_MS}. Deskphone analytics uses this
   * after `hasSession` is already false.
   */
  public hadRecentSipSession(callUuid: string, nowMs = Date.now()): boolean {
    const terminatedAtMs = this._recentlyTerminatedCalls.get(callUuid)
    if (terminatedAtMs === undefined) return false
    if (nowMs - terminatedAtMs >= RECENTLY_TERMINATED_SIP_TTL_MS) {
      this._recentlyTerminatedCalls.delete(callUuid)
      return false
    }
    return true
  }

  private rememberTerminatedSession(
    callUuid: string,
    nowMs = Date.now(),
  ): void {
    this.pruneRecentlyTerminated(nowMs)
    this._recentlyTerminatedCalls.set(callUuid, nowMs)
  }

  private pruneRecentlyTerminated(nowMs = Date.now()): void {
    for (const [uuid, terminatedAtMs] of this._recentlyTerminatedCalls) {
      if (nowMs - terminatedAtMs >= RECENTLY_TERMINATED_SIP_TTL_MS) {
        this._recentlyTerminatedCalls.delete(uuid)
      }
    }
  }

  /**
   * Answer an inbound call.
   * Guards against duplicate accept() calls that would throw
   * "Invalid session state Establishing" if triggered concurrently
   * (e.g., double-click, popup + main window IPC race in Electron).
   * @param callUuid
   * @returns whether the invitation was actually accepted — false when the
   * answer was a no-op (duplicate in flight, session gone, or already past
   * Initial state), so callers can skip their post-answer state updates
   */
  public answerCall(callUuid: string): Promise<boolean> {
    if (this._pendingAnswers.has(callUuid)) {
      this._logger?.warn(
        `answerCall: already answering call ${callUuid}, ignoring duplicate`,
      )
      return Promise.resolve(false)
    }

    const invitation = this._sessions.get(callUuid)

    if (!invitation) {
      this._logger?.warn(
        `answerCall: session ${callUuid} not found (call already terminated), ignoring`,
      )
      return Promise.resolve(false)
    }

    if (!(invitation instanceof Invitation)) {
      this._logger?.warn(
        `answerCall: Session is not an inbound invitation for UUID=${callUuid}`,
      )
      return Promise.resolve(false)
    }

    if (invitation.state !== SessionState.Initial) {
      this._logger?.warn(
        `answerCall: session not in Initial state (current: ${invitation.state}), skipping accept`,
      )
      return Promise.resolve(false)
    }

    this._pendingAnswers.add(callUuid)
    try {
      const req = invitation.accept()
      return req
        .finally(() => {
          this._pendingAnswers.delete(callUuid)
        })
        .then(() => true)
    } catch (e: unknown) {
      this._pendingAnswers.delete(callUuid)
      this._analytics?.track(SipClientAnalyticsEvents.AnswerFailure, {
        call_uuid: callUuid,
      })
      throw e
    }
  }

  /**
   * Reject an inbound call
   * Handles edge cases gracefully: terminated sessions, invalid session types.
   * @param callUuid - The UUID of the call to reject
   * @returns Promise that always resolves (never rejects)
   */
  public async rejectCall(callUuid: string): Promise<void> {
    try {
      const session = this._sessions.get(callUuid)

      if (!session) {
        this._logger?.warn(`rejectCall: Session not found for UUID=${callUuid}`)
        return Promise.resolve()
      }

      if (session.state === SessionState.Terminated) {
        this._logger?.info(
          `rejectCall: Session already terminated, skipping reject for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      if (!this.isValidInvitation(callUuid)) {
        this._logger?.warn(
          `rejectCall: Invalid invitation (not Invitation instance or terminated) for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      const invitation = session as Invitation
      // Marked before the await: `reject()` transitions to Terminated inline,
      // which runs the state listener before this call returns.
      this._locallyTerminatedCalls.add(callUuid)
      await invitation.reject()
      return Promise.resolve()
    } catch (e: unknown) {
      this._logger?.warn('rejectCall caught an error', {
        callUuid,
        error: e,
      })
      this._analytics?.track(SipClientAnalyticsEvents.RejectFailure, {
        call_uuid: callUuid,
      })
      return Promise.resolve()
    }
  }

  /**
   * Starts a call to the target
   * @param callUuid
   * @param target
   * @param displayName
   * @param outboundCallerIdName
   * @returns
   */
  public async startCall(
    callUuid: string,
    target: string,
    displayName?: string,
    outboundCallerIdName?: string,
  ): Promise<OutgoingInviteRequest> {
    this.verifyUserAgent()

    this._logger?.info('sep credentials', {
      target,
      sipDomain: this._sipDomain,
    })
    const uri = UserAgent.makeURI(this.buildDialUri(target))
    if (!uri) {
      const err = new Error('Failed to create URI')
      this._logger?.error('Failed to create URI', err)
      throw err
    }
    const extraHeaders: string[] = []
    if (outboundCallerIdName) {
      extraHeaders.push('X-CALLER-ID: ' + outboundCallerIdName)
    }
    const inviter = new Inviter(this._ua, uri, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
      extraHeaders,
    })
    if (displayName) {
      inviter.remoteIdentity.displayName = displayName
    }

    this._sessions.set(callUuid, inviter)
    this._sessionStates.set(callUuid, inviter.state)

    inviter.stateChange.addListener(
      this.outboundSessionStateListener(callUuid).bind(this),
    )
    this._logger?.info(`Sending INVITE to ${uri}`)

    this.trackCallLifecycle(SipAnalyticsEvent.CallPlaced, {
      direction: 'outbound',
      callUuid,
    })

    this._callSetupSpans.set(callUuid, {
      span: startFlowSpan(TracedFlow.WebphoneCallSetup, {
        direction: 'outbound',
      }),
      startedAtMs: Date.now(),
    })

    try {
      const req = await inviter.invite({
        requestDelegate: {
          onProgress: (response) => {
            this._logger?.info('Outbound call progress response', {
              callUuid,
              statusCode: response.message.statusCode,
            })
            const setup = this._callSetupSpans.get(callUuid)
            if (setup && setup.ringingAtMs === undefined) {
              setup.ringingAtMs = Date.now()
              setup.span.setAttribute(
                'sip.time_to_ring_ms',
                setup.ringingAtMs - setup.startedAtMs,
              )
            }
            this.emit('OutboundCallProgress', {
              callUuid,
              statusCode: response.message.statusCode,
            } as OutboundCallProgressEvent)
          },
        },
      })

      return Promise.resolve(req)
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.StartCallFailure, {
        call_uuid: callUuid,
      })

      // If the session was canceled/terminated, certain errors should be ignored.
      // Read the state off the inviter rather than `_sessions`: Inviter.invite()
      // transitions to Terminated before rethrowing, and that transition runs
      // outboundSessionStateListener inline, which has already deleted the map
      // entry by the time this catch block runs.
      const errorMessage = e instanceof Error ? e.message : String(e)
      const isCanceledOrTerminated =
        inviter.state === SessionState.Terminating ||
        inviter.state === SessionState.Terminated

      // Ignore permission errors when call is canceled/terminated
      const isPermissionError =
        (e instanceof DOMException &&
          (e.name === 'NotAllowedError' ||
            errorMessage?.includes('Permission dismissed'))) ||
        errorMessage?.includes('Permission dismissed')

      // Ignore "Peer connection closed" errors when call is canceled/terminated
      // This prevents unhandled rejections from ICE gathering completing after cancellation
      const isPeerConnectionClosedError = errorMessage?.includes(
        'Peer connection closed',
      )

      if (
        isCanceledOrTerminated &&
        (isPermissionError || isPeerConnectionClosedError)
      ) {
        const errorType = isPermissionError
          ? 'Permission'
          : 'Peer connection closed'
        this._logger?.info(
          `${errorType} error ignored - call was canceled/terminated`,
          { callUuid, error: e },
        )
        // Close the span here rather than leaving it to the Terminated
        // transition: this branch also runs while merely Terminating, and a
        // socket that dies mid-cancel would never deliver Terminated, leaving
        // the span unended and therefore dropped. Same outcome either way —
        // the Terminated path classifies a Terminating teardown as canceled.
        this.endCallSetupSpan(callUuid, 'canceled')
        // Return a resolved promise to prevent unhandled rejection.
        // The call is already being terminated, so this error is expected.
        // The return value is not used by callers when the call is canceled,
        // but we need to return something that matches the expected type.
        // We use the inviter's request message as a fallback.
        return Promise.resolve(
          inviter.request as unknown as OutgoingInviteRequest,
        )
      }

      // An invite() rejection can precede the session state machine engaging,
      // so the Terminated transition that normally ends the span may never
      // arrive. No-ops when that transition already closed it.
      this.endCallSetupSpan(callUuid, 'failed')

      throw e instanceof Error ? e : new Error(String(e))
    }
  }

  public async endCall(callUuid: string): Promise<void> {
    try {
      const session = this.getSession(callUuid)

      // Don't attempt to end a session that's already terminated
      if (session.state === SessionState.Terminated) {
        this._logger?.info('Session already terminated', { callUuid })
        return
      }

      // Don't attempt to cancel a session that's already terminating
      if (session.state === SessionState.Terminating) {
        this._logger?.info('Session already terminating', { callUuid })
        return
      }

      this._locallyTerminatedCalls.add(callUuid)

      if (session.state === SessionState.Established) {
        await this.bye(session)
      } else {
        await this.endEstablishingSession(session)
      }
    } catch (e: unknown) {
      this._logger?.warn('`endCall` caught an error', {
        callUuid,
        error: e,
      })
    }
  }

  private isValidInvitation(callUuid: string): boolean {
    const session = this._sessions.get(callUuid)
    if (!session) {
      return false
    }
    if (!(session instanceof Invitation)) {
      return false
    }
    if (session.state === SessionState.Terminated) {
      return false
    }
    return true
  }

  /**
   * Sends a 180 Ringing provisional for an incoming call we intend to ring.
   * Replaces the stack's auto-provisional (disabled via
   * sendInitialProvisionalResponse) so callers still hear ringback, while
   * busy-rejected calls never emit a 180 first (CUS-591). progress() leaves the
   * session in Initial state, so a later accept()/reject() stays valid.
   * Always resolves; never rejects.
   * @param callUuid - The UUID of the call to ring
   */
  public async progressCall(callUuid: string): Promise<void> {
    try {
      if (!this.isValidInvitation(callUuid)) {
        this._logger?.warn(
          `progressCall: Invalid invitation (not Invitation instance or terminated) for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      const invitation = this._sessions.get(callUuid) as Invitation
      // Only valid from Initial; if we've already accepted/rejected, skip.
      if (invitation.state !== SessionState.Initial) {
        this._logger?.info(
          `progressCall: session not in Initial state (current: ${invitation.state}), skipping progress for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      await invitation.progress()
      this.trackCallLifecycle(SipAnalyticsEvent.InviteReceived, {
        direction: 'inbound',
        callUuid,
      })
      return Promise.resolve()
    } catch (e: unknown) {
      this._logger?.warn('progressCall caught an error', {
        callUuid,
        error: e,
      })
      return Promise.resolve()
    }
  }

  /**
   * Rejects an incoming call with busy signal (486).
   * Handles edge cases gracefully: terminated sessions, invalid session types.
   * @param callUuid - The UUID of the call to reject
   * @returns Promise that always resolves (never rejects)
   */
  public async rejectWithBusy(callUuid: string): Promise<void> {
    try {
      const session = this._sessions.get(callUuid)

      if (!session) {
        this._logger?.warn(
          `rejectWithBusy: Session not found for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      if (session.state === SessionState.Terminated) {
        this._logger?.info(
          `rejectWithBusy: Session already terminated, skipping reject for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      if (!this.isValidInvitation(callUuid)) {
        this._logger?.warn(
          `rejectWithBusy: Invalid invitation (not Invitation instance or terminated) for UUID=${callUuid}`,
        )
        return Promise.resolve()
      }

      const invitation = session as Invitation
      this._locallyTerminatedCalls.add(callUuid)
      await invitation.reject({ statusCode: 486 })
      return Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.SessRejectBusyFailure, {
        call_uuid: callUuid,
      })
      this._logger?.warn('rejectWithBusy caught an error', {
        callUuid,
        error: e,
      })
      return Promise.resolve()
    }
  }

  private async endEstablishingSession(session: Session): Promise<void> {
    if (session instanceof Inviter) {
      return this.cancelSession(session)
    } else if (session instanceof Invitation) {
      return this.reject(session)
    }
  }

  private async cancelSession(session: Inviter): Promise<void> {
    if (session.state === SessionState.Terminated) {
      this._logger?.info('Session already terminated, skipping cancel')
      return Promise.resolve()
    }
    try {
      await session.cancel()
      this._logger?.info('TX CANCEL')
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.SessCancelFailure)
      throw e
    }
  }

  private async reject(session: Invitation): Promise<void> {
    if (session.state === SessionState.Terminated) {
      this._logger?.info('Session already terminated, skipping reject')
      return Promise.resolve()
    }
    try {
      this._logger?.info('TX 486 Busy')
      await session.reject()
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.SessRejectFailure)
      throw e
    }
  }

  private async bye(session: Session): Promise<void> {
    if (session.state === SessionState.Terminated) {
      this._logger?.info('Session already terminated, skipping bye')
      return Promise.resolve()
    }
    try {
      this._logger?.info('TX BYE')
      await session.bye()
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.SessByeFailure)
      throw e
    }
  }

  /**
   * Places call on mute or unmutes call.
   * @param callUuid
   * @param muted
   * @returns
   */
  public muteCall(callUuid: string, muted: boolean): Promise<void> {
    const trackingEvent = muted
      ? SipClientAnalyticsEvents.MuteFailure
      : SipClientAnalyticsEvents.UnmuteFailure
    try {
      if (!this.hasSession(callUuid)) {
        this._logger?.warn(
          `muteCall UUID=${callUuid} session not found, ignoring`,
        )
        return Promise.resolve()
      }

      if (!this.hasPeerConnection(callUuid)) {
        this._logger?.warn(`muteCall UUID=${callUuid} no peer connection`)
        return Promise.resolve()
      }

      const peerConnection = this.getPeerConnection(callUuid)
      peerConnection.getSenders().forEach((sender: RTCRtpSender) => {
        if (sender.track) {
          sender.track.enabled = !muted
        }
      })
    } catch (e: unknown) {
      this._analytics?.track(trackingEvent, {
        call_uuid: callUuid,
      })
      throw e
    }

    this._logger?.info(`muteCall UUID=${callUuid} Muted=${muted}`)

    return Promise.resolve()
  }

  /**
   * Places call on hold or unholds call.
   *
   * Only one re-INVITE per call may be outstanding: sip.js rejects a second
   * `Session.invite()` with "Reinvite in progress" until the first transaction
   * gets its final response, and `invite()` resolves as soon as the request is
   * *sent*, a full round trip before that. So the guard is released from the
   * request delegate (onAccept/onReject), not when this method returns.
   * @param callUuid
   * @param hold
   * @returns the re-INVITE request, or undefined when the call already has an
   * unsettled hold/resume in flight and this one was ignored. Nothing reached
   * the wire in that case, so callers must skip any optimistic hold-state
   * update — see the early return in `holdCallInternal` (hooks/usePhone.ts).
   */
  public async holdCall(
    callUuid: string,
    hold: boolean,
  ): Promise<OutgoingInviteRequest | undefined> {
    const session = this.getSession(callUuid)

    // Ensure session is in Established state before attempting hold/unhold
    if (session.state !== SessionState.Established) {
      const error = new Error(
        `Cannot hold/unhold call: session is not in Established state. Current state: ${session.state}`,
      )
      this._logger?.error('holdCall: Session not established', error, {
        callUuid,
        hold,
        sessionState: session.state,
      })
      throw error
    }

    if (this._pendingHoldOperations.has(callUuid)) {
      this._logger?.warn(
        `holdCall: re-INVITE already in progress for UUID=${callUuid}, ignoring Hold=${hold}`,
      )
      return undefined
    }

    // Claimed here, immediately after the check, so nothing can interleave
    // between the two. Released from the request delegate below.
    const pendingHold = this.beginPendingHold(callUuid)

    type HoldModifier = (
      description: RTCSessionDescriptionInit,
    ) => Promise<RTCSessionDescriptionInit>

    const holdModifier: HoldModifier = (description) => {
      if (!description.sdp || !description.type) {
        throw new Error('Invalid SDP: missing sdp or type')
      }

      let sdp = description.sdp
      const type = description.type

      // CRITICAL: Only modify OFFER, not ANSWER
      // When we send a re-INVITE with hold, we send an offer with a=sendonly
      // The remote responds with an answer that has a=recvonly (which is correct)
      // If we modify the answer, we'll create incompatible directions
      if (type === 'answer') {
        // Don't modify answers - they come from the remote and should be accepted as-is
        return Promise.resolve({ sdp, type })
      }

      if (hold) {
        // Putting call on hold: we want to send only (sendonly)
        // If no direction attributes exist, add sendonly after each m-line
        if (!/a=(sendrecv|sendonly|recvonly|inactive)/.test(sdp)) {
          sdp = sdp.replace(/(m=[^\r]*\r\n)/g, '$1a=sendonly\r\n')
        } else {
          // Replace existing direction attributes with sendonly
          sdp = sdp.replace(/a=sendrecv\r\n/g, 'a=sendonly\r\n')
          sdp = sdp.replace(/a=recvonly\r\n/g, 'a=sendonly\r\n')
          sdp = sdp.replace(/a=inactive\r\n/g, 'a=sendonly\r\n')
        }
      } else {
        // Unholding call: we want bidirectional (sendrecv)
        // If no direction attributes exist, add sendrecv after each m-line
        if (!/a=(sendrecv|sendonly|recvonly|inactive)/.test(sdp)) {
          sdp = sdp.replace(/(m=[^\r]*\r\n)/g, '$1a=sendrecv\r\n')
        } else {
          // Replace existing direction attributes with sendrecv
          sdp = sdp.replace(/a=sendonly\r\n/g, 'a=sendrecv\r\n')
          sdp = sdp.replace(/a=recvonly\r\n/g, 'a=sendrecv\r\n')
          sdp = sdp.replace(/a=inactive\r\n/g, 'a=sendrecv\r\n')
        }
      }

      return Promise.resolve({ sdp, type })
    }

    const options: SessionInviteOptions = {
      sessionDescriptionHandlerModifiers: [holdModifier],
      requestDelegate: {
        onAccept: () => {
          this.endPendingHold(callUuid, pendingHold)
          this._logger?.info(
            `holdCall: Hold operation accepted UUID=${callUuid} Hold=${hold}`,
          )
        },
        onReject: (response) => {
          this.endPendingHold(callUuid, pendingHold)
          this._logger?.warn(
            `holdCall: Hold operation rejected UUID=${callUuid} Hold=${hold} Status=${response.message.statusCode || 'unknown'} Reason=${response.message.reasonPhrase || 'unknown'}`,
          )
        },
      },
    }

    try {
      this._logger?.info(`holdCall UUID=${callUuid} Hold=${hold}`)
      const req = await session.invite(options)
      return req
    } catch (e: unknown) {
      this.endPendingHold(callUuid, pendingHold)
      const error = e as Error

      // An overlapping re-INVITE is transient, not a hold failure: nothing
      // reached the wire and the session is untouched. Warn (not error) so we
      // don't fire a Sentry event via captureException, and skip hold_failure.
      // Must downgrade here because the caller can't un-fire an event this layer
      // already raised on the way up.
      if (e instanceof RequestPendingError) {
        this._logger?.warn(
          `holdCall: re-INVITE already in progress for UUID=${callUuid}, Hold=${hold} not sent`,
          { callUuid, hold, errorMessage: error.message },
        )
        throw e
      }

      this._logger?.error('hold invite failed', error, {
        callUuid,
        hold,
        errorMessage: error.message,
        errorStack: error.stack,
      })
      this._analytics?.track(SipClientAnalyticsEvents.HoldFailure, {
        call_uuid: callUuid,
      })
      throw e
    }
  }

  /**
   * Blind transfer to target
   * @param callUuid
   * @param target
   * @returns
   */
  public async blindTransferCall(
    callUuid: string,
    target: string,
  ): Promise<OutgoingInviteRequest> {
    const session = this.getSession(callUuid)
    let start = 0
    let end = target.length
    while (start < end && target[start] === ',') start++
    while (end > start && target[end - 1] === ',') end--
    const sanitizedTarget = target.slice(start, end)
    const sipTarget = this.buildDialUri(sanitizedTarget)
    try {
      const uri = UserAgent.makeURI(sipTarget)
      if (!uri) {
        throw new Error(`invalid target: ${sipTarget}`)
      }
      this._logger?.info(
        `transferCall UUID=${callUuid} sip target=${sipTarget}`,
      )
      this._logger?.info(`transferCall UUID=${callUuid} target=${target}`)
      this._analytics?.track(SipClientAnalyticsEvents.TransferRequest, {
        call_uuid: callUuid,
      })
      const req = await session.refer(uri)
      return req
    } catch (e: unknown) {
      this._logger?.error(
        `transfer call UUID=${callUuid} target=${sipTarget}`,
        e as Error,
      )
      this._analytics?.track(SipClientAnalyticsEvents.TransferFailure, {
        call_uuid: callUuid,
      })
      throw e
    }
  }

  /**
   * Attended transfer to Established call
   * @param callUuid
   * @param targetCallUuid
   * @returns
   */
  public async attendedTransferCall(
    callUuid: string,
    targetCallUuid: string,
  ): Promise<OutgoingInviteRequest> {
    const session = this.getSession(callUuid)
    const targetSession = this.getSession(targetCallUuid)
    if (targetSession.state !== SessionState.Established) {
      throw new Error(
        `Attented transfer from ${callUuid} to unestablished session ${targetCallUuid}`,
      )
    }
    try {
      this._logger?.info(
        `attendedTransferCall UUID=${callUuid} targetUUID=${targetCallUuid}`,
      )
      const req = await session.refer(targetSession)
      return req
    } catch (e: unknown) {
      // write to log and send to sentry
      this._logger?.error(
        `attended transfer call UUID=${callUuid} targetUUID=${targetCallUuid}`,
      )
      this._analytics?.track(SipClientAnalyticsEvents.AttendedTransferFailure, {
        call_uuid: callUuid,
      })
      throw e
    }
  }

  /**
   * Passes DTMF digit to the session using SIP INFO
   * @param callUuid
   * @param digit
   * @returns
   */
  public async sendDtmf(
    callUuid: string,
    digit: string,
  ): Promise<OutgoingInfoRequest> {
    const session = this.getSession(callUuid)
    const options = {
      requestOptions: {
        body: {
          contentDisposition: 'render',
          contentType: 'application/dtmf-relay',
          content: `Signal=${digit}\r\nDuration=${DTMF_DURATION}`,
        },
      },
    }
    try {
      this._logger?.info(`sendDtmf UUID=${callUuid} digit=${digit}`)
      const req = await session.info(options)
      return req
    } catch (e: unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.DtmfFailure, {
        call_uuid: callUuid,
      })
      this._logger?.error('dtmf failed', e as Error)
      throw e
    }
  }

  /**
   * Introspects session to parse display name or user name from the remote identity
   * @param callUuid
   * @returns
   */
  public getRemoteCallerId(callUuid: string): {
    displayName: string
    friendlyName: string
    user: string | undefined
  } {
    const session = this.getSession(callUuid)
    const remoteIdentity = session.remoteIdentity
    return {
      displayName: remoteIdentity.displayName,
      friendlyName: remoteIdentity.friendlyName,
      user: remoteIdentity.uri.user,
    }
  }

  private isPageOrIntercomCall(invitation: Invitation): boolean {
    const alertInfo = invitation.request.getHeader('Alert-Info')
    const callInfo = invitation.request.getHeader('Call-Info')
    return (
      (alertInfo?.includes('answer-after=0') ?? false) ||
      (callInfo?.includes('answer-after=0') ?? false)
    )
  }

  onInvite(invitation: Invitation): void {
    const callUuid = invitation.id.substring(0, 36)
    this._logger?.info(`onInvite UUID=${callUuid}`)
    // sipc_inv_received is emitted from progressCall after 180 Ringing, not
    // here. Raw INVITE includes auto-rejects and UA-down paths that never rang.

    // Verify UserAgent is connected before processing invitation
    if (!this._ua.isConnected()) {
      this._logger?.warn(
        `onInvite UUID=${callUuid} - UserAgent not connected, rejecting call`,
      )
      invitation.reject({ statusCode: 503 }).catch((e) => {
        this._logger?.error(
          'Failed to reject call due to connection issue',
          e as Error,
        )
      })
      return
    }

    const isClickToCall =
      invitation.request.getHeader('X-Click-To-Call')?.toLowerCase() ===
        'true' || isClickToCallName(invitation.remoteIdentity.displayName)
    const isPageOrIntercom = this.isPageOrIntercomCall(invitation)
    const queueId = invitation.request.getHeader('Queue-Id') ?? undefined

    this._sessions.set(callUuid, invitation)
    this._sessionStates.set(callUuid, invitation.state)
    invitation.delegate = {
      onCancel: () => this._remotelyCanceledCalls.add(callUuid),
    }

    this.emit('IncomingInviteReceived', {
      callUuid: callUuid,
      number: invitation.remoteIdentity.uri.user ?? 'Unknown Number',
      name: invitation.remoteIdentity.displayName,
      isClickToCall,
      isPageOrIntercom,
      queueId,
    })

    invitation.stateChange.addListener(
      this.inboundSessionStateListener(callUuid).bind(this),
    )
  }

  async onConnect(): Promise<void> {
    if (this._connectionGuard.checkAborted()) {
      this._logger?.debug('Connect event ignored - connection already aborted')
      return
    }

    this._connectionGuard.transitionTo(ConnectionState.CONNECTED)
    this._logger?.info('Transport Connected')
    this.emit('TransportConnected')

    // Create a new Registerer every time a connection is made
    const registerer = new Registerer(this._ua)
    this._registerer = registerer

    // Add the listener for state changes
    const registrationId = crypto.randomUUID()
    registerer.stateChange.addListener(
      this.registrationStateListener(registrationId),
    )

    try {
      this._logger?.info('Registration request')
      await registerer.register({ requestDelegate: this })
    } catch (e: Error | unknown) {
      this._analytics?.track(SipClientAnalyticsEvents.RegFailure, {
        registration_id: registrationId,
      })
      this._logger?.error('Registration failed', e as Error)
    }
  }

  onDisconnect(error?: Error | undefined): void {
    if (this._connectionGuard.checkAborted()) {
      this._logger?.debug(
        'Disconnect event ignored - connection already aborted',
      )
      return
    }

    this._connectionGuard.transitionTo(ConnectionState.DISCONNECTED)
    this._logger?.info('TransportDisconnected')
    // Note: we deliberately do NOT stop quality monitors here. Signaling
    // transport loss doesn't mean the media (RTCPeerConnection) is gone — the
    // call often survives a reconnect. Monitors self-stop when the peer
    // connection actually goes closed/failed; full teardown is handled by
    // dispose().

    if (
      !this._connectionGuard.isDisposed &&
      this._registerer?.state === RegistererState.Registered
    ) {
      void this._registerer
        .unregister()
        .then(() => {
          this._logger?.info('Unregister succeeded after TransportDisconnected')
        })
        .catch(() => {
          this._logger?.info('Unregister failed after TransportDisconnected')
        })
    } else {
      this._logger?.info(
        `Unregister skipped after TransportDisconnected. isDisposed=${this._connectionGuard.isDisposed} state=${this._registerer?.state}`,
      )
    }

    if (this._connectionGuard.canTransition) {
      this.emit('TransportDisconnected', error)
    }
  }

  onReject(response: IncomingResponse): void {
    this._logger?.info('RegistrationRejected')
    this.emit('RegistrationRejected', response.message.reasonPhrase)
  }
}
